import Phaser from 'phaser';
import { CREDITS } from '@/data/credits';

const FONT_MONO = '"Courier New", monospace';
const FONT_IMPACT = 'Impact, "Arial Black", sans-serif';

const SCROLL_SPEED = 60; // px per second
const AUTO_RETURN_MS = 60_000; // fallback: return to title after 60 s

/**
 * CreditsScene — MY-1685.
 *
 * Shown after Stage 9 finale victory, and reachable from Title → CREDITS.
 * Scrolls credits text upward. Any key / timer returns to TitleScene.
 */
export class CreditsScene extends Phaser.Scene {
  private creditsContainer!: Phaser.GameObjects.Container;
  private returnTimer!: Phaser.Time.TimerEvent;
  private variant: 'normal' | 'sellout' = 'normal';

  constructor() {
    super({ key: 'CreditsScene' });
  }

  init(data?: { variant?: string }): void {
    this.variant = data?.variant === 'sellout' ? 'sellout' : 'normal';
  }

  create(): void {
    const { width, height } = this.scale;

    // Dark background
    this.add.rectangle(width / 2, height / 2, width, height, 0x080c12);

    this.creditsContainer = this.add.container(0, 0);
    let yOffset = height + 40;

    const isSellout = this.variant === 'sellout';
    const titleText = isSellout ? 'SELL-OUT' : 'MANOR';
    const titleColor = isSellout ? '#ffba08' : '#ef233c';

    // Title line
    const title = this.add
      .text(width / 2, yOffset, titleText, {
        fontFamily: FONT_IMPACT,
        fontSize: '80px',
        color: titleColor,
        stroke: '#080c12',
        strokeThickness: 8,
      })
      .setOrigin(0.5, 0);
    this.creditsContainer.add(title);
    yOffset += 100;

    if (isSellout) {
      const badEnding = this.add
        .text(width / 2, yOffset, 'BAD ENDING', {
          fontFamily: FONT_MONO,
          fontSize: '28px',
          color: '#ef233c',
          letterSpacing: 6,
        })
        .setOrigin(0.5, 0);
      this.creditsContainer.add(badEnding);
      yOffset += 50;

      const flavour = this.add
        .text(width / 2, yOffset, '"You took the money.\nLondon got what it deserved."', {
          fontFamily: FONT_MONO,
          fontSize: '20px',
          color: '#888888',
          align: 'center',
          lineSpacing: 8,
        })
        .setOrigin(0.5, 0);
      this.creditsContainer.add(flavour);
      yOffset += 80;
    }

    for (const section of CREDITS) {
      const headingText = this.add
        .text(width / 2, yOffset, section.heading, {
          fontFamily: FONT_MONO,
          fontSize: '22px',
          color: '#ffba08',
          letterSpacing: 4,
        })
        .setOrigin(0.5, 0);
      this.creditsContainer.add(headingText);
      yOffset += 36;

      for (const line of section.lines) {
        const lineText = this.add
          .text(width / 2, yOffset, line, {
            fontFamily: FONT_MONO,
            fontSize: '18px',
            color: '#aaaaaa',
            letterSpacing: 2,
          })
          .setOrigin(0.5, 0);
        this.creditsContainer.add(lineText);
        yOffset += 28;
      }

      yOffset += 32;
    }

    // Final tagline
    const tagline = this.add
      .text(width / 2, yOffset, '— LONDON BELONGS TO ITS PEOPLE —', {
        fontFamily: FONT_MONO,
        fontSize: '14px',
        color: '#555555',
        letterSpacing: 3,
      })
      .setOrigin(0.5, 0);
    this.creditsContainer.add(tagline);

    // Any key returns to title
    this.input.keyboard?.once('keydown', () => this.returnToTitle());

    // Fallback timer
    this.returnTimer = this.time.addEvent({
      delay: AUTO_RETURN_MS,
      callback: this.returnToTitle,
      callbackScope: this,
    });
  }

  update(_time: number, delta: number): void {
    this.creditsContainer.y -= (SCROLL_SPEED * delta) / 1000;

    // Auto-return once the last line scrolls off the top
    if (this.creditsContainer.y + this.creditsContainer.getBounds().height < 0) {
      this.returnToTitle();
    }
  }

  private returnToTitle(): void {
    if (!this.scene.isActive('CreditsScene')) return;
    this.returnTimer?.remove(false);
    this.scene.start('TitleScene');
  }
}
